// src/components/modals/DeckUploadModal.js
import React, { useState, useRef } from 'react';
import Modal from '../shared/Modal';
import Button from '../shared/Button';
import './DeckUploadModal.css';

/**
 * Modal for uploading custom card decks
 */
function DeckUploadModal({ show, onClose, onUpload }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [deckData, setDeckData] = useState(null);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      setDeckData(null);
      setError('');
      return;
    }

    // Reset state
    setError('');
    setDeckData(null);

    // Validate file type
    if (!file.name.endsWith('.json')) {
      setError('Please select a JSON file (.json)');
      setSelectedFile(null);
      return;
    }

    // Validate file size (2MB max)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      setError(
        `File too large (${(file.size / 1024 / 1024).toFixed(2)} MB). Maximum size is 2 MB.`
      );
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);

    // Parse JSON
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);

      // Basic structure validation
      if (!parsed.name || typeof parsed.name !== 'string') {
        throw new Error('Deck must have a "name" field');
      }
      if (!Array.isArray(parsed.blackCards)) {
        throw new Error('Deck must have a "blackCards" array');
      }
      if (!Array.isArray(parsed.whiteCards)) {
        throw new Error('Deck must have a "whiteCards" array');
      }

      setDeckData(parsed);
    } catch (err) {
      setError(`Invalid JSON file: ${err.message}`);
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
    if (!deckData) return;

    setIsUploading(true);
    try {
      await onUpload(deckData);
      // Success - close modal and reset state
      handleClose();
    } catch (err) {
      // Error handling is done by parent component
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setDeckData(null);
    setError('');
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={isUploading}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleUpload}
        disabled={!deckData || isUploading}
        loading={isUploading}
      >
        {isUploading ? 'Uploading...' : 'Upload Deck'}
      </Button>
    </>
  );

  return (
    <Modal
      show={show}
      onClose={handleClose}
      title="📁 Upload Custom Deck"
      footer={footer}
      size="md"
      contentClassName="deck-upload-modal"
      showCloseButton={!isUploading}
    >
      <p>Upload a JSON file containing your custom card deck:</p>

      {/* File Input */}
      <div className="file-input-container">
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileSelect}
          className="file-input"
          id="deck-file-input"
          disabled={isUploading}
        />
        <label htmlFor="deck-file-input" className="file-input-label">
          {selectedFile ? '📄 Change File' : '📂 Choose JSON File'}
        </label>
      </div>

      {/* Selected File Info */}
      {selectedFile && !error && (
        <div className="file-info">
          <span className="file-name">{selectedFile.name}</span>
          <span className="file-size">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="upload-error">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Deck Preview */}
      {deckData && !error && (
        <div className="deck-preview">
          <h4>Deck Preview:</h4>
          <div className="deck-preview-content">
            <div className="preview-field">
              <strong>Name:</strong> {deckData.name}
            </div>
            {deckData.description && (
              <div className="preview-field">
                <strong>Description:</strong> {deckData.description}
              </div>
            )}
            <div className="preview-field">
              <strong>Question Cards:</strong> {deckData.blackCards.length}
            </div>
            <div className="preview-field">
              <strong>Answer Cards:</strong> {deckData.whiteCards.length}
            </div>
          </div>
          <div className="preview-note">
            <span className="info-icon">ℹ️</span>
            <span>Server will validate card format and content</span>
          </div>
        </div>
      )}

      {/* Upload Instructions */}
      {!selectedFile && !error && (
        <div className="upload-instructions">
          <h4>Deck Format Requirements:</h4>
          <ul>
            <li>File must be valid JSON (.json)</li>
            <li>Maximum file size: 2 MB</li>
            <li>Minimum 10 question cards, 50 answer cards</li>
            <li>Maximum 500 question cards, 2000 answer cards</li>
            <li>Question cards must have "text" and "blanks" fields</li>
            <li>Answer cards must be text strings</li>
          </ul>
        </div>
      )}
    </Modal>
  );
}

export default DeckUploadModal;
